import { useAuthDIContext } from '@/features/auth';
import { useAppDispatch } from '@/platform';
import { Permission, setPermissions } from '@/shared/auth';
import {
  logout as logoutAction,
  setAccount,
  setIsAuthenticated,
  setRole,
  setUser,
} from '@/shared/infra/store/authSlice';
import React, { useEffect, useState } from 'react';
import { announceIdentity } from './crossTabIdentity';

/** Never leave the app blank if the probe hangs. */
const PROBE_TIMEOUT_MS = 3000;

interface SessionBootstrapProps {
  children: React.ReactNode;
}

/**
 * Re-derives who the user is from the browser's session cookie, before anything renders.
 *
 * The cookie that authorises requests is shared by every tab; the client's picture of the user is
 * persisted per TAB. So a new tab shows a login screen while the session is live, and a tab whose
 * cookie was taken by a second sign-in keeps rendering the previous user while acting as the new
 * one. Asking the server on load makes the credential the single source of truth, which is the only
 * thing that stays correct across tabs, reloads, and a session revoked out from under us.
 *
 * **Children are gated on the probe rather than on a `loading` flag.** `ProtectedRoute` decides
 * whether to redirect the moment it renders, so anything that lets it run first re-introduces the
 * very bounce this removes — and a persisted `loading: false` would win over an initial `true`.
 * Rendering nothing until the answer is in removes the race outright.
 *
 * A 401 is the expected answer for a signed-out visitor, not an error: the probe is exempt from the
 * HttpClient interceptor that would otherwise send them to /login, which is what keeps candidates on
 * the public Section 1, remote-I9 and remediation screens where they belong.
 */
export default function SessionBootstrap({ children }: SessionBootstrapProps) {
  const dispatch = useAppDispatch();
  const authDIContext = useAuthDIContext();
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const settle = () => {
      if (!cancelled) setSettled(true);
    };
    // A hung probe must never leave a blank page — but rendering is all the timeout buys. The
    // persisted identity goes with it, because a timeout is NOT the inconclusive case the .catch
    // below forgives: there a request completed, here nothing completed and nothing retries, so
    // "briefly trusting what we had" would be indefinite. Clearing it at the source keeps every
    // consumer honest rather than just ProtectedRoute, and costs the public screens nothing — they
    // carry no identity to lose. A late answer is not cancelled, and signs the tab back in.
    const timer = window.setTimeout(() => {
      if (cancelled) return;
      dispatch(logoutAction());
      settle();
    }, PROBE_TIMEOUT_MS);

    authDIContext!.authUseCase
      .getSession()
      .then((session) => {
        if (cancelled) return;
        dispatch(setUser(session.user));
        dispatch(setAccount(session.account));
        dispatch(setRole(session.role));
        dispatch(setPermissions(session.permissions as Permission[]));
        dispatch(setIsAuthenticated(true));
        // Keep the cross-tab watcher pointed at whoever the cookie actually belongs to.
        announceIdentity(session.user?.appUserId ?? null);
      })
      .catch((error: { status?: number }) => {
        if (cancelled) return;
        // 401 → genuinely signed out, so drop any identity this tab had persisted; it is stale by
        // definition. Anything else (offline, a 5xx) is inconclusive, and signing someone out over a
        // blip would be worse than briefly trusting what we had.
        if (error?.status === 401) dispatch(logoutAction());
      })
      .finally(() => {
        window.clearTimeout(timer);
        settle();
      });

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // Once, on mount: this establishes the session the rest of the app renders against.
  }, [authDIContext, dispatch]);

  if (!settled) return null;
  return <>{children}</>;
}
